import type { FieldSpec } from '@moment/horizon-data-access/platform'
import type {
  ConcreteObjectiveTerm,
  EvaluatedObjectiveTerm,
  FilterDictionary,
  ObjectiveComponentDetail,
  ObjectiveTerm,
  ObjectiveTermOperation,
} from '@moment/horizon-data-access/proposal'
import type { SecurityTaxInputs } from './security-tax-expressions'
import type { QuoteSideKey, SecurityRow } from './security-types'

import { Operator } from '@moment/trading-types/filter'

import {
  DURATION_COLUMN,
  securityExpressionRef,
} from './security-duration-column'
import {
  EFFECTIVE_TAX_RATE_COLUMN,
  isTaxColumn,
  renderTaxColumn,
  taxYieldColumn,
} from './security-tax-expressions'
import { UnsupportedSecurityTranslationError } from './security-types'
import {
  bookFields,
  evalFields,
  MID_EVAL_FIELDS,
  SECURITY_ID_FIELD,
} from './security-wire-fields'
import { securityFieldForInstrumentField } from './translate-filter-object'

export const MAX_OBJECTIVE_TERMS = 20
const MAX_COMPUTED_COLUMNS = 64
export const OBJECTIVE_SCORE_ALIAS = 'objective_score'

const LEAF_ALIAS_PREFIX = 'objective_leaf_'

export interface ObjectiveTranslationOptions {
  /** Book side the bonds are scored against: `ask` for buys, `bid` otherwise. */
  side: QuoteSideKey
  /** Evals-as-orders orgs price from the side's MPE, never the book. */
  useEvalsAsOrders: boolean
  /** Whether the runtime Security schema serves a field. */
  isFieldAvailable: (field: string) => boolean
  /** The investor's tax inputs, inlined into tax-aware yield leaves. */
  tax?: SecurityTaxInputs
}

type PlannedTerm =
  | {
      kind: 'leaf'
      alias: string
      field: string
      factor: number
      filter?: FilterDictionary
      // Renders NULL where its filter matches: a tax leaf without its inputs.
      unavailable: boolean
    }
  | {
      kind: 'operation'
      operation: ObjectiveTermOperation
      left: PlannedTerm
      right: PlannedTerm
      factor: number
    }

interface PlannedComponent {
  factor: number
  term: PlannedTerm
}

export interface ObjectivePlan {
  /** Computed columns to add to the request: one per leaf, plus the score. */
  fields: FieldSpec[]
  components: PlannedComponent[]
  /** Mapped leaf fields scored as zero because the deployed schema does not serve them yet; tax-derived leaves are NULL instead. */
  unresolvedFields: string[]
}

/**
 * Translates objective terms to SORM computed columns over Security, mirroring
 * live inventory's objective: each leaf is `CASE WHEN filter THEN
 * COALESCE(source, 0.0) ELSE 0 END`, combined by the term's operations.
 */
export function translateObjective(
  terms: ReadonlyArray<ObjectiveTerm>,
  options: ObjectiveTranslationOptions,
): ObjectivePlan {
  if (terms.length > MAX_OBJECTIVE_TERMS) {
    throw new UnsupportedSecurityTranslationError(
      `An objective takes at most ${MAX_OBJECTIVE_TERMS} terms; got ${terms.length}`,
    )
  }
  const leafFields: FieldSpec[] = []
  const unresolvedFields: string[] = []
  const leafExprs = new Map<string, string>()

  const plan = (term: ConcreteObjectiveTerm): PlannedTerm => {
    if (term.type === 'operation') {
      return {
        kind: 'operation',
        operation: term.operation,
        left: plan(term.left),
        right: plan(term.right),
        factor: 1,
      }
    }
    const alias = `${LEAF_ALIAS_PREFIX}${leafFields.length}`
    const sources = objectiveSources(term.field, options)
    if (term.field !== 'constant' && sources.length === 0) {
      throw new UnsupportedSecurityTranslationError(
        `No Security field for objective field "${term.field}"`,
      )
    }
    const available = sources
      .map((source) => sourceRef(source, options))
      .filter((ref) => ref !== null)
    // A tax-derived leaf without its inputs leaves the score unavailable.
    const unavailableTax =
      available.length === 0 && sources.length > 0 && sources.every(isTaxColumn)
    if (
      term.field !== 'constant' &&
      available.length === 0 &&
      !unavailableTax
    ) {
      unresolvedFields.push(term.field)
    }
    const value = unavailableTax ? 'NULL' : leafValue(term.field, available)
    const expr = term.filter
      ? `CASE WHEN ${filterCondition(term.filter, options)} THEN ${value} ELSE 0.0 END`
      : value
    leafExprs.set(alias, expr)
    leafFields.push({ alias, expr })
    return {
      kind: 'leaf',
      alias,
      field: term.field,
      factor: term.factor ?? 1,
      filter: term.filter,
      unavailable: unavailableTax,
    }
  }

  const components = terms.map((term) => ({
    factor: term.factor ?? 1,
    term: plan(term.term),
  }))
  const readsDuration = leafFields.some((leaf) =>
    leaf.expr?.includes(DURATION_COLUMN),
  )
  const columnBudget = MAX_COMPUTED_COLUMNS - (readsDuration ? 1 : 0)
  if (leafFields.length + 1 > columnBudget) {
    throw new UnsupportedSecurityTranslationError(
      `An objective renders at most ${columnBudget - 1} leaves; got ${leafFields.length}`,
    )
  }

  const scoreExpr =
    components.length === 0
      ? '0.0'
      : components
          .map((c) => withFactor(termExpr(c.term, leafExprs), c.factor))
          .join(' + ')

  return {
    fields: [...leafFields, { alias: OBJECTIVE_SCORE_ALIAS, expr: scoreExpr }],
    components,
    unresolvedFields,
  }
}

/** Security fields or tax columns (`isTaxColumn`) a leaf reads, most preferred first. */
export function objectiveSources(
  field: string,
  {
    side,
    useEvalsAsOrders,
  }: Pick<ObjectiveTranslationOptions, 'side' | 'useEvalsAsOrders'>,
): string[] {
  const book = bookFields(side)
  const evals = evalFields(side)
  const fromBook = (bookField: string) => (useEvalsAsOrders ? [] : [bookField])
  switch (field) {
    case 'constant':
      return []
    case 'order_price':
      return [...fromBook(book.price), evals.price, MID_EVAL_FIELDS.price]
    case 'order_ytw':
      return [...fromBook(book.ytw), evals.ytw, MID_EVAL_FIELDS.ytw]
    case 'order_ytm':
      return [...fromBook(book.ytm), evals.ytm, MID_EVAL_FIELDS.ytm]
    case 'post_tax_limit_ytw':
      return [
        ...fromBook(taxYieldColumn('best', side, 'postTaxYtw')),
        taxYieldColumn('mpe', side, 'postTaxYtw'),
      ]
    case 'tax_effective_limit_ytw':
      return [
        ...fromBook(taxYieldColumn('best', side, 'taxEquivalentYield')),
        taxYieldColumn('mpe', side, 'taxEquivalentYield'),
      ]
    case 'effective_tax_rate':
      return [EFFECTIVE_TAX_RATE_COLUMN]
    case 'spread_to_benchmark':
      return ['mpe_mid_ytw_spread_bps']
    case 'fixed_transaction_cost_in_yield':
      return ['fixed_transaction_cost_in_yield']
    default: {
      const mapped = securityFieldForInstrumentField(field, side)
      return mapped ? [mapped] : []
    }
  }
}

// A tax column inlines its expression; null when the schema cannot serve it.
function sourceRef(
  source: string,
  { isFieldAvailable, tax }: ObjectiveTranslationOptions,
): string | null {
  if (isTaxColumn(source)) {
    return renderTaxColumn(source, tax ?? {}, isFieldAvailable)
  }
  return isFieldAvailable(source) ? fieldRef(source) : null
}

function leafValue(field: string, refs: string[]): string {
  if (field === 'constant') return '1.0'
  if (refs.length === 0) return '0.0'
  return `COALESCE(${refs.join(', ')}, 0.0)`
}

function termExpr(term: PlannedTerm, leafExprs: Map<string, string>): string {
  if (term.kind === 'leaf') {
    return withFactor(`(${leafExprs.get(term.alias)})`, term.factor)
  }
  const left = termExpr(term.left, leafExprs)
  const right = termExpr(term.right, leafExprs)
  const combined = (() => {
    switch (term.operation) {
      case 'add':
        return `(${left} + ${right})`
      case 'subtract':
        return `(${left} - ${right})`
      case 'multiply':
        return `(${left} * ${right})`
      case 'divide':
        return `COALESCE(${left} / NULLIF(${right}, 0), 0.0)`
      case 'max':
        return `GREATEST(${left}, ${right})`
      case 'min':
        return `LEAST(${left}, ${right})`
    }
  })()
  return withFactor(combined, term.factor)
}

function withFactor(expr: string, factor: number): string {
  return factor === 1 ? expr : `(${expr} * ${factor})`
}

function fieldRef(field: string): string {
  return securityExpressionRef(field)
}

function stringLiteral(value: string): string {
  return `'${value.replaceAll("'", "''")}'`
}

function literal(value: unknown): string {
  if (typeof value === 'number') return String(value)
  if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE'
  if (typeof value === 'string') return stringLiteral(value)
  throw new UnsupportedSecurityTranslationError(
    `Unsupported objective filter value ${JSON.stringify(value)}`,
  )
}

const COMPARISONS: Partial<Record<string, string>> = {
  [Operator.EqualTo]: '=',
  [Operator.NotEqualTo]: '!=',
  [Operator.GreaterThan]: '>',
  [Operator.GreaterThanOrEqualTo]: '>=',
  [Operator.LessThan]: '<',
  [Operator.LessThanOrEqualTo]: '<=',
}

/** Renders an objective term's filter as an exprdsl boolean expression. */
export function filterCondition(
  filter: FilterDictionary,
  options: Pick<ObjectiveTranslationOptions, 'side' | 'isFieldAvailable'>,
): string {
  const parts: string[] = []
  for (const [key, value] of Object.entries(filter)) {
    if (value === undefined) continue
    if (
      key === 'and' ||
      key === 'and_list' ||
      key === 'or' ||
      key === 'or_list'
    ) {
      const children = (value as FilterDictionary[]).map(
        (child) => `(${filterCondition(child, options)})`,
      )
      if (children.length === 0) continue
      const joiner = key.startsWith('and') ? ' AND ' : ' OR '
      parts.push(`(${children.join(joiner)})`)
      continue
    }
    if (key === 'not' || key === 'not_filter') {
      parts.push(
        `(NOT (${filterCondition(value as FilterDictionary, options)}))`,
      )
      continue
    }
    parts.push(
      ...fieldConditions(key, value as Record<string, unknown>, options),
    )
  }
  return parts.length === 0 ? 'TRUE' : parts.join(' AND ')
}

function fieldConditions(
  field: string,
  condition: Record<string, unknown>,
  options: Pick<ObjectiveTranslationOptions, 'side' | 'isFieldAvailable'>,
): string[] {
  const securityField =
    field === 'isin'
      ? SECURITY_ID_FIELD
      : securityFieldForInstrumentField(field, options.side)
  if (!securityField || !options.isFieldAvailable(securityField)) {
    throw new UnsupportedSecurityTranslationError(
      `No Security field for objective filter field "${field}"`,
    )
  }
  const ref = fieldRef(securityField)
  return Object.entries(condition)
    .filter(([, value]) => value !== undefined)
    .map(([operator, value]) => {
      const comparison = COMPARISONS[operator]
      if (comparison) return `${ref} ${comparison} ${literal(value)}`
      switch (operator as Operator) {
        case Operator.In:
        case Operator.NotIn: {
          const values = value as unknown[]
          const negate = operator === Operator.NotIn
          if (values.length === 0) return negate ? 'TRUE' : 'FALSE'
          return `${ref} ${negate ? 'NOT IN' : 'IN'} (${values.map(literal).join(', ')})`
        }
        case Operator.IsNull:
          return value ? `${ref} IS NULL` : `${ref} IS NOT NULL`
        case Operator.InFilterList:
          return `IN_FILTER_LIST(${ref}, ${stringLiteral(String(value))})`
        case Operator.NotInFilterList:
          return `(NOT IN_FILTER_LIST(${ref}, ${stringLiteral(String(value))}))`
        default:
          throw new UnsupportedSecurityTranslationError(
            `No Security translation for objective filter operator "${operator}"`,
          )
      }
    })
}

function numberOrZero(value: unknown): number {
  if (value === null || value === undefined) return 0
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function evaluate(term: PlannedTerm, row: SecurityRow): EvaluatedObjectiveTerm {
  if (term.kind === 'leaf') {
    return {
      type: 'field',
      field: term.field,
      factor: term.factor,
      ...(term.filter ? { filter: term.filter } : {}),
      value: numberOrZero(row[term.alias]) * term.factor,
    }
  }
  const left = evaluate(term.left, row)
  const right = evaluate(term.right, row)
  return {
    type: 'operation',
    operation: term.operation,
    left,
    right,
    value:
      applyOperation(term.operation, left.value, right.value) * term.factor,
  }
}

function applyOperation(
  operation: ObjectiveTermOperation,
  left: number,
  right: number,
): number {
  switch (operation) {
    case 'add':
      return left + right
    case 'subtract':
      return left - right
    case 'multiply':
      return left * right
    case 'divide':
      return right === 0 ? 0 : left / right
    case 'max':
      return Math.max(left, right)
    case 'min':
      return Math.min(left, right)
  }
}

export interface ObjectiveScore {
  /** Null when a leaf the row needs is unavailable; never a partial sum. */
  objective_score: number | null
  objective_score_decomposition: ObjectiveComponentDetail[]
}

function readsUnavailableLeaf(term: PlannedTerm, row: SecurityRow): boolean {
  if (term.kind === 'leaf') {
    return term.unavailable && (row[term.alias] ?? null) === null
  }
  return (
    readsUnavailableLeaf(term.left, row) ||
    readsUnavailableLeaf(term.right, row)
  )
}

/** Builds a row's objective score and its decomposition from the leaf columns. */
export function scoreObjectiveRow(
  plan: ObjectivePlan,
  row: SecurityRow,
): ObjectiveScore {
  const decomposition = plan.components.map((component) => {
    const term = evaluate(component.term, row)
    return {
      factor: component.factor,
      term,
      value: term.value * component.factor,
    }
  })
  return {
    objective_score: objectiveScore(plan, row, decomposition),
    objective_score_decomposition: decomposition,
  }
}

function objectiveScore(
  plan: ObjectivePlan,
  row: SecurityRow,
  decomposition: ObjectiveComponentDetail[],
): number | null {
  if (
    plan.components.some((component) =>
      readsUnavailableLeaf(component.term, row),
    )
  ) {
    return null
  }
  const serverScore = row[OBJECTIVE_SCORE_ALIAS]
  if (serverScore === null || serverScore === undefined) {
    return decomposition.reduce((sum, detail) => sum + detail.value, 0)
  }
  return numberOrZero(serverScore)
}
